import apiClient from './apiClient';
import type {
  RockUCSConformityReading,
  RockUCSData,
  RockUCSGeneral,
  RockUCSReadingsPayload,
  RockUCSUltrasonicReading,
  RockUCSUploadResult,
} from '../types/rockUcs';

export const getRockUcsData = async (workOrderDetailKey: string): Promise<RockUCSData> => {
  const r = await apiClient.get<RockUCSData>(`/workorderdetails/${workOrderDetailKey}/rockucs`);
  return r.data;
};

export const saveRockUcsData = async (
  workOrderDetailKey: string,
  general: RockUCSGeneral,
  conformityReadings: RockUCSConformityReading[],
  ultrasonicReadings: RockUCSUltrasonicReading[],
  confirmReset?: boolean,
): Promise<RockUCSData> => {
  const r = await apiClient.put(`/workorderdetails/${workOrderDetailKey}/rockucs`, {
    general,
    conformityReadings,
    ultrasonicReadings,
    confirmReset,
  });
  return r.data;
};

export const getRockUcsReadings = async (workOrderDetailKey: string): Promise<RockUCSReadingsPayload> => {
  const r = await apiClient.get<RockUCSReadingsPayload>(`/workorderdetails/${workOrderDetailKey}/rockucs/readings`);
  return r.data;
};

/** One reading row posted by the editable Test Data grid (raw inputs + machine passthrough). */
export interface RockUCSReadingEdit {
  timeS: number | null;
  frameLoadKN: number | null;
  axialDisp1Mm: number | null;
  axialDisp2Mm: number | null;
  radialStrainPct: number | null;
  machineDeviatorStressMPa: number | null;
  // machineConfiningPressureMPa intentionally omitted — uniaxial test (σ₃ = 0).
  // See item 1.5 DROP decision.
  machinePorePressureMPa: number | null;
  machineAxialStrainPct: number | null;
  machineVolStrainPct: number | null;
}

/**
 * Save a hand-edited readings set (add / edit / delete rows). The backend
 * recomputes every derived column + the peak/summary, then returns the fresh
 * payload. A 409 `TEST_REQUIRES_RESET` is surfaced for protected statuses —
 * retry with `confirmReset = true`.
 */
export const saveRockUcsReadings = async (
  workOrderDetailKey: string,
  rows: RockUCSReadingEdit[],
  confirmReset?: boolean,
): Promise<RockUCSReadingsPayload & { resetPerformed?: boolean }> => {
  const r = await apiClient.put<RockUCSReadingsPayload & { resetPerformed?: boolean }>(
    `/workorderdetails/${workOrderDetailKey}/rockucs/readings`,
    { rows, confirmReset },
  );
  return r.data;
};

/** Upload a GCTS CATS CSV — sent as text in the JSON body (≈100 KB files). */
export const uploadRockUcsCsv = async (
  workOrderDetailKey: string,
  file: File,
): Promise<RockUCSUploadResult> => {
  const content = await file.text();
  const r = await apiClient.post<RockUCSUploadResult>(
    `/workorderdetails/${workOrderDetailKey}/rockucs/upload`,
    { fileName: file.name, content },
  );
  return r.data;
};
